import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import MediaComponent from '../Media/media-component.js';
import { getAboutData } from '../../Public/about-data.js';
import { ButtonPrimary } from '../Buttons/button-component.js';
import SystemDataService from '../../Services/SystemDataService.js';
import { ASSETS } from '../../Public/assets-data.js';

/**
 * "Who We Are", editorial About section (ClearViolations light theme).
 *
 * Left column: eyebrow + two-line display headline (orange line / navy line),
 * lead paragraph and a primary CTA. Below it a photo card with an orange
 * "years of experience" badge, beside a dark navy mission card that can cycle
 * through several `missions` via the prev/next arrows.
 *
 * Fully prop-driven, all copy/links live in about-data (whoWeAreSection).
 */
export default function WhoWeAreSection({ whoWeAreSection }) {
    const { language = 'he', dir = 'rtl' } = useSelector((state) => state.systemData || {});
    const navigate = useNavigate();
    const isRtl = dir !== 'ltr';

    // Single source of truth: the public site content (`content.aboutData` from
    // S3), falling back to the local seed, so the landing and About page always
    // show identical "Who We Are" content. Uses the PUBLIC content endpoint
    // (getByPath is admin-gated and 401s for visitors).
    const [remoteAbout, setRemoteAbout] = useState(null);
    useEffect(() => {
        let mounted = true;
        SystemDataService.getSystemContent()
            .then((c) => {
                if (mounted && c?.aboutData?.whoWeAreSection && Object.keys(c.aboutData.whoWeAreSection).length) {
                    setRemoteAbout(c.aboutData);
                    console.log('[WhoWeAre] source: S3 (content.aboutData.whoWeAreSection)');
                } else {
                    console.log('[WhoWeAre] source: LOCAL seed (S3 whoWeAreSection empty/missing)');
                }
            })
            .catch((e) => console.log('[WhoWeAre] source: LOCAL seed (fetch failed)', e?.message));
        return () => { mounted = false; };
    }, [language]);

    const localWhoWeAre = useMemo(() => getAboutData(language)?.whoWeAreSection, [language]);
    const source = whoWeAreSection || remoteAbout?.whoWeAreSection || localWhoWeAre;

    const {
        eyebrow = 'Who We Are',
        headingLine1 = 'Crafting Excellence',
        headingLine2 = 'in Every Project',
        description = 'We combine deep industry knowledge, hands-on experience, and a commitment to doing things the right way. Every project. Every time.',
        ctaText = 'Learn More',
        ctaHref = '/services',
        image = '/placeholder.svg',
        imageAlt = 'Our team',
        badgeValue = '25+',
        badgeLabel = 'Years\nof Experience',
        decorImage,
        missions = [],
    } = source || {};

    // Support a single-object `mission` too, by normalising to an array.
    const slides = missions.length
        ? missions
        : [source?.mission].filter(Boolean);
    const [active, setActive] = useState(0);
    const [paused, setPaused] = useState(false); // stops autoplay once the user interacts
    const total = slides.length;

    const prev = useCallback(() => {
        if (total <= 1) return;
        setActive((i) => (i - 1 + total) % total);
    }, [total]);
    const next = useCallback(() => {
        if (total <= 1) return;
        setActive((i) => (i + 1) % total);
    }, [total]);

    const userPrev = () => { setPaused(true); prev(); };
    const userNext = () => { setPaused(true); next(); };
    const goTo = (i) => { setPaused(true); setActive(i); };

    // Swipe to change the mission sentence (mobile + mouse drag).
    const swipeX = useRef(null);
    const onSwipeDown = (e) => { swipeX.current = e.clientX; setPaused(true); };
    const onSwipeEnd = (e) => {
        if (swipeX.current == null) return;
        const dx = e.clientX - swipeX.current;
        swipeX.current = null;
        if (Math.abs(dx) < 40) return;
        const forward = isRtl ? dx > 0 : dx < 0; // swipe toward the reading direction → next
        forward ? next() : prev();
    };

    // Auto-rotate the missions every 5s until the user interacts with the arrows.
    useEffect(() => {
        if (paused || total <= 1) return;
        const id = setInterval(() => next(), 5000);
        return () => clearInterval(id);
    }, [paused, total, next]);

    const slide = slides[active] || {};
    const go = (href) => {
        if (!href) return;
        if (href.startsWith('/')) navigate(href);
        else window.location.href = href;
    };

    const Arrow = isRtl ? ArrowLeft : ArrowRight;

    return (
        <section dir={dir} className="relative w-full overflow-hidden bg-[#F4F5F7] py-16 md:py-12">
            {/* faint dot texture, matching the rest of the ClearViolations light sections */}
            <div className="pointer-events-none absolute inset-0 opacity-[0.03] bg-[radial-gradient(#0B2265_1px,transparent_1px)] [background-size:40px_40px]" />

            {/* Mobile watermark — full-bleed, pinned to the very top of the section,
                behind the content (text reads over it). */}
            <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 -top-10 z-0 select-none lg:hidden">
                <div className="w-full opacity-[0.15]">
                    <MediaComponent
                        image={ASSETS.MAESTROSHIELD_FLORIDA}
                        alt=""
                        width={800}
                        useSekeleton={false}
                        objectFit="contain"
                        wrapperClassName="w-full"
                        className="h-auto w-full"
                    />
                </div>
            </div>

            {/* Low-opacity brand watermark — aligned to the content container's
                right edge (not the viewport) so it sits neatly in the corner. */}
            <div dir="ltr" aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 z-0 hidden select-none lg:block">
                <div className="relative mx-auto max-w-[1240px] px-6">
                    <div className="absolute -right-4 -top-16 w-[560px] opacity-20 xl:w-[680px] 2xl:w-[760px]">
                        <MediaComponent
                            image={ASSETS.MAESTROSHIELD_FLORIDA}
                            alt=""
                            width={900}
                            useSekeleton={false}
                            objectFit="contain"
                            wrapperClassName="w-full"
                            className="h-auto w-full"
                        />
                    </div>
                </div>
            </div>

            {/* crane-hook accent, hangs from the very top of the section, aligned to the
                content container's right edge so it sits above the mission card. The inline
                `right` keeps it physically right regardless of RTL logical-property remapping. */}
            {decorImage && (
                <div dir="ltr" aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 z-0 hidden select-none lg:block">
                    <div className="relative mx-auto max-w-[1240px] px-6">
                        <div className="absolute top-0 w-[340px] xl:w-[400px]" style={{ right: '1.5rem' }}>
                            <MediaComponent
                                image={decorImage}
                                alt=""
                                useSekeleton={false}
                                objectFit="contain"
                                wrapperClassName="w-full"
                                className="h-auto w-full"
                            />
                        </div>
                    </div>
                </div>
            )}

            <div className="relative z-10 mx-auto max-w-[1240px] px-6">
                {/* ── Top: intro copy ── */}
                <div className="max-w-[640px]">
                    {/* <div className="flex items-center gap-3">
                        <span className="font-secondary text-sm font-bold uppercase tracking-[0.18em] text-primary-500">
                            {eyebrow}
                        </span>
                    </div> */}

                    <h2 className="mt-5 font-secondary text-[40px] font-extrabold leading-[1.05] tracking-[-0.02em] sm:text-[52px] md:text-[58px]">
                        <span className="block text-secondary-500">{headingLine1}</span>
                        <span className="block text-primary-500">{headingLine2}</span>
                    </h2>

                    <p className="mt-6 max-w-[520px] whitespace-pre-line text-lg leading-relaxed text-ink-muted">
                        {description}
                    </p>

                    <div className="mt-8 max-w-xs mx-auto" >
                        {ctaText && (
                            <ButtonPrimary
                                type="button"
                                href={ctaHref}
                            >
                                {ctaText}
                                {/* <Arrow className="h-5 w-5" /> */}
                            </ButtonPrimary>
                        )}
                    </div>
                </div>

                {/* ── Bottom: photo + mission card ── */}
                <div className="mt-12 grid grid-cols-1 items-stretch gap-6 md:mt-16 lg:grid-cols-2">
                    {/* Photo with experience badge */}
                    <div className="relative overflow-hidden rounded-3xl bg-primary-100 shadow-xl shadow-primary-950/10">
                        <MediaComponent
                            image={image}
                            alt={imageAlt}
                            wrapperClassName="w-full h-full min-h-[420px] lg:min-h-[500px]"
                            className="h-full w-full object-cover"
                            useSekeleton={false}
                        />
                        {badgeValue && (
                            <div className={`absolute bottom-6 flex items-center gap-3 rounded-2xl bg-secondary-500 px-6 py-4 text-white shadow-lg ${isRtl ? 'right-6' : 'left-6'}`}>
                                <span className="font-secondary text-4xl font-extrabold leading-none md:text-5xl">
                                    {badgeValue}
                                </span>
                                <span className="whitespace-pre-line text-sm font-semibold leading-tight">
                                    {badgeLabel}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Mission card, swipeable on mobile to change the sentence */}
                    <div
                        className="relative flex select-none flex-col justify-between overflow-hidden rounded-3xl bg-primary-500 p-8 md:p-10"
                        style={{ touchAction: 'pan-y' }}
                        onMouseEnter={() => setPaused(true)}
                        onMouseLeave={() => setPaused(false)}
                        onPointerDown={total > 1 ? onSwipeDown : undefined}
                        onPointerUp={total > 1 ? onSwipeEnd : undefined}
                    >
                        <style>{`@keyframes wwaSlideIn{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:none}}`}</style>

                        <div className="relative z-10">
                            {/* keyed so each mission animates in as it becomes active */}
                            <p
                                key={active}
                                className="min-h-[132px] text-lg leading-relaxed text-white/85 md:text-xl"
                                style={{ animation: 'wwaSlideIn 0.5s ease-out' }}
                            >
                                {slide.body}
                            </p>
                        </div>

                        <div className="relative z-10 mt-8 flex items-center justify-between">
                            <div className="flex flex-col gap-4">
                                {slide.linkText && (
                                    <button
                                        key={`link-${active}`}
                                        type="button"
                                        onClick={() => go(slide.linkHref)}
                                        className="inline-flex items-center gap-2 font-secondary font-semibold text-secondary-500 transition-colors hover:text-secondary-400"
                                        style={{ animation: 'wwaSlideIn 0.5s ease-out' }}
                                    >
                                        {slide.linkText}
                                        <Arrow className="h-4 w-4" />
                                    </button>
                                )}

                                {/* pagination dots */}
                                {total > 1 && (
                                    <div className="flex items-center gap-2">
                                        {slides.map((_, i) => (
                                            <button
                                                key={i}
                                                type="button"
                                                aria-label={`Go to mission ${i + 1}`}
                                                aria-current={i === active}
                                                onClick={() => goTo(i)}
                                                className={`h-2 rounded-full transition-all duration-300 ${i === active ? 'w-6 bg-secondary-500' : 'w-2 bg-white/30 hover:bg-white/50'}`}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>

                            {total > 1 && (
                                <div className="flex items-center gap-3">
                                    <button
                                        type="button"
                                        aria-label="Previous"
                                        onClick={userPrev}
                                        className="flex h-11 w-11 items-center justify-center rounded-full border border-white/25 text-white transition-colors hover:bg-white/10"
                                    >
                                        {isRtl ? <ArrowRight className="h-5 w-5" /> : <ArrowLeft className="h-5 w-5" />}
                                    </button>
                                    <button
                                        type="button"
                                        aria-label="Next"
                                        onClick={userNext}
                                        className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary-500 text-white transition-colors hover:bg-secondary-600"
                                    >
                                        {isRtl ? <ArrowLeft className="h-5 w-5" /> : <ArrowRight className="h-5 w-5" />}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
